import { useState, useEffect, useRef, useCallback, Suspense } from 'react'
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as api from '../services/api.js'
import { getMenuItems } from '../services/posts.js'
import { getBlocks } from '../services/blocks.js'
import { getMyCart } from '../services/cart.js'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import useLocalizedPath from '../hooks/useLocalizedPath'
import LanguageSwitcher from './LanguageSwitcher'
import AnalyticsConsent from './AnalyticsConsent'
import ChatWidget from './ChatWidget'

export default function Layout() {
  const { lang } = useParams()
  const { t, i18n } = useTranslation()
  const localizedPath = useLocalizedPath()
  const { isAdmin, isEditor } = useAuth()
  const [user, setUser] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const navigate = useNavigate()
  const { theme, toggleTheme, isDark } = useTheme()
  const [siteSettings, setSiteSettings] = useState(null)
  const [headerItems, setHeaderItems] = useState([])
  const [socialItems, setSocialItems] = useState([])
  const [footerItems, setFooterItems] = useState([])
  const [blocks, setBlocks] = useState([])
  const [cartCount, setCartCount] = useState(0)
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [newsletterStatus, setNewsletterStatus] = useState(null)
  const [challengeSvg, setChallengeSvg] = useState('')
  const [challengeToken, setChallengeToken] = useState('')
  const [challengeCode, setChallengeCode] = useState('')
  const [challengeError, setChallengeError] = useState(false)

  // Sync i18next language with URL param
  useEffect(() => {
    if (lang && ['en', 'es'].includes(lang)) {
      i18n.changeLanguage(lang)
    }
  }, [lang, i18n])

  const getSocialIcon = (name) => {
    const lower = name?.toLowerCase() || ''
    if (lower.includes('facebook')) {
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
    }
    if (lower.includes('x') || lower.includes('twitter')) {
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.865 11.924-3.648 4.923-10.158-12.578L2.31 18.76l6.76-4.692L7.5 22H2.25L9.918 9.75l7.326 5.562-2.472 6.938H18.75l-7.227-8.26L11.005 2.25H18.244z" /></svg>
    }
    if (lower.includes('instagram')) {
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" /><circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" /><circle cx="18" cy="6" r="1" /></svg>
    }
    if (lower.includes('youtube')) {
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" /><polygon points="10 9 10 15 15 12 10 9" fill="#fff" /></svg>
    }
    if (lower.includes('tiktok')) {
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.79 1.79 2.89 2.89 0 0 1 2.31-4.59 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43V7.64a7.65 7.65 0 0 1-1 .05v1.76a4.85 4.85 0 0 0 4.14 4.76 4.84 4.84 0 0 1-3.88-4.47z" /></svg>
    }
    if (lower.includes('github')) {
      return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" /></svg>
    }
    if (lower.includes('drupal')) {
      return <svg width="20" height="20" viewBox="0 0 640 640" fill="currentColor"><path d="M400.3 172.1C364.5 136.5 330.5 102.4 320.4 64C310.4 102.4 276.1 136.5 240.4 172.1C186.8 225.7 126.1 286.4 126.1 377.4C123.7 484.7 208.8 573.6 316.1 576C423.4 578.4 512.3 493.2 514.6 385.9C514.7 383.1 514.7 380.3 514.6 377.4C514.6 286.3 453.8 225.7 400.3 172.1zM226 395.1C220.1 402.6 215 410.7 210.8 419.3C210.5 420 210 420.6 209.5 421.1C209 421.6 208.3 421.9 207.5 422.1L205.8 422.1C201.5 422.1 196.6 413.6 196.6 413.6C195.3 411.6 194.1 409.4 192.9 407.2L192.1 405.4C180.9 379.7 190.6 343.1 190.6 343.1C195.4 325.3 203.3 308.4 213.8 293.2C220.2 283.6 227.3 274.3 234.8 265.5L244 274.7L287.5 319.1C288.3 320 288.8 321.2 288.8 322.4C288.8 323.6 288.3 324.8 287.5 325.7L242.1 376.3L226 395.1zM322.6 522.4C309.7 522.3 297 518.5 286.2 511.4C275.4 504.3 266.8 494.3 261.6 482.5C256.4 470.7 254.6 457.6 256.6 444.8C258.6 432 264.2 420.1 272.8 410.4C287 393.5 304.3 376.9 323.1 355.1C345.4 378.9 360 395.2 374.3 413.1C375.4 414.5 376.4 416 377.3 417.5C385.1 428.6 389.3 441.9 389.3 455.5C389.3 464.3 387.6 472.9 384.3 481C381 489.1 376.1 496.5 369.9 502.7C363.7 508.9 356.4 513.8 348.3 517.2C340.2 520.6 331.6 522.3 322.8 522.3L322.6 522.3zM448.7 415.6C448.3 417 447.5 418.2 446.4 419.2C445.3 420.2 444 420.8 442.6 421L441.3 421C438.7 420.1 436.4 418.3 435 415.9C423.4 398.4 410.1 382 395.5 366.9L377.7 348.5L318.6 287.2C306.2 275.7 294.4 263.6 283.2 250.9C282.9 250.4 282.6 250 282.3 249.5C280.3 246.7 278.7 243.6 277.6 240.3L277.6 238.5C276.8 233.5 277.2 228.4 278.8 223.5C280.4 218.6 283.2 214.3 286.8 210.8C298.2 199.4 309.8 187.8 320.6 175.9C332.6 189.2 345.4 201.9 358 214.5C383.8 238.5 407.1 265 427.6 293.6C444.9 318.1 454.3 347.4 454.6 377.4C454.5 390.3 452.4 403.2 448.6 415.6z" /></svg>
    }
    return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10" /></svg>
  }

  // Load site settings from MongoDB (once + on visibility change)
  useEffect(() => {
    const abortController = new AbortController();

    const loadSettings = async () => {
      try {
        const settings = await api.getSiteSettings({ signal: abortController.signal });
        if (settings) setSiteSettings(settings);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to load site settings:', err);
        }
      }
    };

    loadSettings();

    const onVisible = () => {
      if (document.visibilityState === 'visible') loadSettings();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      abortController.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Load header menu items (once + on visibility change)
  useEffect(() => {
    const abortController = new AbortController();

    const loadMenu = async () => {
      try {
        const items = await getMenuItems('principal', { signal: abortController.signal });
        setHeaderItems(items);
        const social = await getMenuItems('social', { signal: abortController.signal });
        setSocialItems(social);
        const footer = await getMenuItems('footer', { signal: abortController.signal });
        setFooterItems(footer);
        const publishedBlocks = await getBlocks();
        setBlocks(publishedBlocks);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to load menu items:', err);
        }
      }
    };

    loadMenu();

    const onVisible = () => {
      if (document.visibilityState === 'visible') loadMenu();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      abortController.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('user')
    if (stored) {
      try { setUser(JSON.parse(stored)) } catch { }
    }
  }, [])

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    let tick = 0
    const checkUser = setInterval(() => {
      tick++
      const stored = localStorage.getItem('user')
      if (!stored) {
        if (user) setUser(null)
        return
      }
      try {
        const parsed = JSON.parse(stored)
        if (!user || user.objectId !== parsed.objectId || user.username !== parsed.username || user.email !== parsed.email || user.role !== parsed.role || user.avatar !== parsed.avatar) {
          setUser(parsed)
        }
      } catch { }

      // Every 60s, re-fetch profile from DB to catch admin edits
      if (tick % 60 === 0 && user) {
        api.getMyProfile().then(profile => {
          if (!profile) return
          const stored = localStorage.getItem('user')
          if (!stored) return
          const current = JSON.parse(stored)
          if (current.email !== profile.email || current.username !== profile.username || current.role !== profile.role || current.avatar !== profile.avatar) {
            const updated = { ...current, email: profile.email, username: profile.username, role: profile.role, avatar: profile.avatar }
            localStorage.setItem('user', JSON.stringify(updated))
            setUser(updated)
          }
        }).catch(() => {})
      }
    }, 1000)
    return () => clearInterval(checkUser)
  }, [user])

  const refreshCartCount = useCallback(() => {
    if (!user) { setCartCount(0); return }
    getMyCart().then(data => {
      const count = data?.items?.reduce((s, i) => s + i.quantity, 0) || 0
      setCartCount(count)
    }).catch(() => setCartCount(0))
  }, [user])

  useEffect(() => {
    refreshCartCount()
    window.addEventListener('cart:updated', refreshCartCount)
    return () => window.removeEventListener('cart:updated', refreshCartCount)
  }, [refreshCartCount])

  const handleLogout = () => {
    localStorage.removeItem('user')
    setUser(null)
    setMenuOpen(false)
    setMobileMenuOpen(false)
    navigate(localizedPath('/'))
    window.location.reload()
  }

  const updateUserData = () => {
    const stored = localStorage.getItem('user')
    if (stored) {
      try { setUser(JSON.parse(stored)) } catch { }
    }
    setMenuOpen(false)
  }

  const loadNewsletterChallenge = async () => {
    setNewsletterStatus('loading-captcha')
    setChallengeError(false)
    try {
      const data = await api.getChallenge()
      setChallengeSvg(data.svg)
      setChallengeToken(data.token)
      setChallengeCode('')
      setNewsletterStatus(null)
    } catch {
      setChallengeError(true)
      setNewsletterStatus('error')
    }
  }

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault()
    if (!newsletterEmail) return
    if (!challengeToken) {
      loadNewsletterChallenge()
      return
    }
    setNewsletterStatus('loading')
    try {
      const result = await api.subscribeToNewsletter(newsletterEmail, challengeCode, challengeToken)
      if (result.success) {
        if (result.pending) {
          setNewsletterStatus('pending')
        } else {
          setNewsletterStatus('success')
          setNewsletterEmail('')
        }
      } else {
        setNewsletterStatus('error')
      }
    } catch {
      setNewsletterStatus('error')
    }
  }

  return (
    <>
      <header className="header">
        <div className="container">
          <div className="header-left">
            <Link to={localizedPath('/')} className="logo">
              <img className="header-logo"
                src="/logo.png"
                alt={import.meta.env.VITE_SITE_NAME}
              />
              {import.meta.env.VITE_SITE_NAME}
            </Link>

            {/* Mobile header actions */}
            <div className="mobile-header-actions">
              <Link
                to={user ? localizedPath('/cart') : localizedPath('/login')}
                className="mobile-cart-btn"
                title={t('layout.shoppingCart')}
                style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                {cartCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '-4px', right: '-4px',
                    background: 'var(--primary)', color: '#fff',
                    borderRadius: '50%', width: '16px', height: '16px',
                    fontSize: '10px', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontWeight: 'bold',
                  }}>{cartCount}</span>
                )}
              </Link>
              <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
                <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
                <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
                <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
              </button>
            </div>
          </div>

          <div className="header-right">
            {/* Desktop nav */}
            <nav className="header-nav">
              <Link
                to={user ? localizedPath('/cart') : localizedPath('/login')}
                className="btn btn-outline btn-sm"
                title={t('layout.shoppingCart')}
                style={{ padding: '0.5rem', position: 'relative' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
                {cartCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '-4px', right: '-4px',
                    background: 'var(--primary)', color: '#fff',
                    borderRadius: '50%', width: '16px', height: '16px',
                    fontSize: '10px', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontWeight: 'bold',
                  }}>{cartCount}</span>
                )}
              </Link>
              <LanguageSwitcher />
              <button
                onClick={toggleTheme}
                className="btn btn-outline btn-sm"
                title={isDark ? t('layout.switchToLight') : t('layout.switchToDark')}
                style={{ padding: '0.5rem' }}
              >
                {isDark ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="5" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                )}
              </button>
              <NavLink end to={localizedPath('/blog')}>{t('layout.blog')}</NavLink>
              <NavLink end to={localizedPath('/store')}>{t('layout.store')}</NavLink>
              {headerItems.length > 0 && headerItems.map(item => {
                const absUrl = item.url && !item.url.startsWith('http') && !item.url.startsWith('/') ? '/' + item.url : item.url
                return item.url.startsWith('http')
                  ? <a key={item.objectId} href={item.url} target="_blank" rel="noopener noreferrer">{item.labelTranslations?.[i18n.language] || item.label}</a>
                  : <NavLink end key={item.objectId} to={localizedPath(absUrl)}>{item.labelTranslations?.[i18n.language] || item.label}</NavLink>
              })}
            </nav>

            {/* Desktop user menu */}
            <div className="header-actions">
              {user ? (
                <div className="user-menu" ref={menuRef}>
                  <button className="user-menu-trigger" onClick={() => setMenuOpen(!menuOpen)}>
                    {user.avatar ? (
                      <img src={user.avatar} alt="" className="user-avatar" />
                    ) : (
                      <span className="user-avatar">{user.username.charAt(0).toUpperCase()}</span>
                    )}
                    <span className={`user-menu-arrow ${menuOpen ? 'open' : ''}`}>▾</span>
                  </button>
                  {menuOpen && (
                    <div className="user-menu-dropdown">
                      <div className="user-menu-info">
                        {user.avatar ? (
                          <img src={user.avatar} alt="" className="user-avatar" />
                        ) : (
                          <span className="user-avatar">{user.username.charAt(0).toUpperCase()}</span>
                        )}
                        <span>{user.username}</span>
                      </div>
                      <div className="user-menu-divider"></div>
                      <button className="user-menu-item" onClick={() => { navigate(localizedPath(isAdmin || isEditor ? '/admin' : '/dashboard')); setMenuOpen(false) }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>
                        {t('layout.dashboard')}
                      </button>
                      <button className="user-menu-item" onClick={() => { navigate(localizedPath('/settings')); setMenuOpen(false) }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.604.852.997 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                        {t('layout.settings')}
                      </button>
                      <div className="user-menu-divider"></div>
                      <button className="user-menu-item user-menu-logout" onClick={handleLogout}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                        {t('layout.logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to={localizedPath('/login')} className="btn btn-outline btn-sm">{t('layout.login')}</Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)}></div>

      {/* Mobile drawer */}
      <aside className={`mobile-menu-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <LanguageSwitcher />
            <button
              onClick={toggleTheme}
              className="btn btn-outline btn-sm"
              title={isDark ? t('layout.switchToLight') : t('layout.switchToDark')}
              style={{ padding: '0.5rem' }}
            >
              {isDark ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5" /><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
          </div>
          <button className="mobile-menu-close" onClick={() => setMobileMenuOpen(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <nav className="mobile-menu-nav">
          <NavLink end to={localizedPath('/blog')} onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>{t('layout.blog')}</NavLink>
          <NavLink end to={localizedPath('/store')} onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>{t('layout.store')}</NavLink>
          {headerItems.length > 0 ? headerItems.map(item => (
            item.url.startsWith('http')
              ? <a key={item.objectId} href={item.url} target="_blank" rel="noopener noreferrer">{item.labelTranslations?.[i18n.language] || item.label}</a>
              : <NavLink end key={item.objectId} to={localizedPath(item.url)} onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>{item.labelTranslations?.[i18n.language] || item.label}</NavLink>
          )) : <NavLink end to={localizedPath('/')} onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => isActive ? 'active' : ''}>{t('layout.blog')}</NavLink>}
        </nav>
        {user ? (
          <div className="mobile-menu-footer">
              <div className="mobile-user-info">
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="user-avatar" />
                ) : (
                  <span className="user-avatar">{user.username.charAt(0).toUpperCase()}</span>
                )}
                <span>{user.username}</span>
            </div>
            <div className="mobile-menu-actions">
              <Link to={localizedPath(isAdmin || isEditor ? '/admin' : '/dashboard')} onClick={() => setMobileMenuOpen(false)}>{t('layout.dashboard')}</Link>
              <Link to={localizedPath('/settings')} onClick={() => setMobileMenuOpen(false)}>{t('layout.settings')}</Link>
              <button onClick={handleLogout}>{t('layout.logout')}</button>
            </div>
          </div>
        ) : (
          <div className="mobile-menu-footer">
            <Link to={localizedPath('/login')} onClick={() => setMobileMenuOpen(false)} className="btn btn-primary" style={{ width: '100%' }}>{t('layout.login')}</Link>
          </div>
        )}
      </aside>

      <main>
        <Suspense fallback={<div className="loading"></div>}>
          <Outlet context={{ updateUserData }} />
        </Suspense>
      </main>

      {blocks.length > 0 && (
        <div className="blocks-section">
          {blocks.map(block => {
            const bt = block.translations?.[lang]
            return (
              <div key={block.objectId} className="newsletter-section">
                <div className="newsletter-card">
                  {(bt?.title || block.title) && !block.hideTitle && <h3>{bt?.title || block.title}</h3>}
                  {(bt?.content || block.content) && (
                    <div className="block-content" dangerouslySetInnerHTML={{ __html: bt?.content || block.content }} />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="newsletter-section">
        <div className="newsletter-card">
          <h3>{t('newsletter.title')}</h3>
          <p className="newsletter-subtitle">{t('newsletter.subtitle')}</p>
          <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
            <input
              type="email"
              value={newsletterEmail}
              onChange={e => setNewsletterEmail(e.target.value)}
              placeholder={t('newsletter.placeholder')}
              required
              disabled={newsletterStatus === 'loading' || newsletterStatus === 'loading-captcha'}
            />
            <button type="submit" className="btn btn-primary" disabled={newsletterStatus === 'loading' || newsletterStatus === 'loading-captcha'}>
              {newsletterStatus === 'loading'
                ? '...'
                : newsletterStatus === 'loading-captcha'
                  ? '...'
                  : challengeToken ? t('newsletter.confirmSubscribe') : t('newsletter.subscribe')}
            </button>
          </form>
          {challengeToken && newsletterStatus !== 'pending' && (
            <div className="newsletter-challenge">
              {challengeError ? (
                <p className="alert alert-error">{t('newsletter.captchaLoadError')} <button type="button" className="btn btn-sm" onClick={loadNewsletterChallenge}>{t('newsletter.retry')}</button></p>
              ) : challengeSvg ? (
                <>
                  <label>{t('newsletter.confirmCaptcha')}</label>
                  <div className="captcha-row">
                    <img
                      src={`data:image/svg+xml;base64,${btoa(challengeSvg)}`}
                      alt="Captcha"
                      style={{ borderRadius: '4px', border: '1px solid #ddd', background: '#f4f4f4' }}
                    />
                    <button type="button" className="btn btn-sm" onClick={loadNewsletterChallenge} title={t('newsletter.refreshCaptcha')}>↻</button>
                  </div>
                  <input
                    className="newsletter-captcha-input"
                    type="text"
                    value={challengeCode}
                    onChange={e => setChallengeCode(e.target.value)}
                    placeholder={t('newsletter.captchaPlaceholder')}
                    required
                    autoComplete="off"
                    maxLength={5}
                    style={{ textTransform: 'uppercase' }}
                  />
                </>
              ) : (
                <p>{t('newsletter.loadingCaptcha')}</p>
              )}
            </div>
          )}
          {newsletterStatus === 'pending' && (
            <p className="newsletter-feedback success">{t('newsletter.confirmSent')}</p>
          )}
          {newsletterStatus === 'success' && (
            <p className="newsletter-feedback success">{t('newsletter.success')}</p>
          )}
          {newsletterStatus === 'error' && (
            <p className="newsletter-feedback error">{t('newsletter.error')}</p>
          )}
        </div>
      </div>

      <footer className="footer">
        <div className="container">
          {socialItems.length > 0 && (
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1rem' }}>
              {socialItems.map((item) => (
                <a
                  key={item.objectId}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)', transition: 'color 0.2s' }}
                  onMouseEnter={(e) => e.target.style.color = 'var(--primary)'}
                  onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
                >
                  {getSocialIcon(item.label)}
                </a>
              ))}
            </div>
          )}
          {footerItems.length > 0 && (
            <p style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {footerItems.map((item, idx) => (
                <span key={item.objectId}>
                  {idx > 0 && <span style={{ color: 'var(--text-muted)' }}> | </span>}
                  {(() => {
                    const absUrl = item.url && !item.url.startsWith('http') && !item.url.startsWith('/') ? '/' + item.url : item.url
                    return absUrl?.startsWith('/') ? (
                      <NavLink
                        to={localizedPath(absUrl)}
                        end
                        style={({ isActive }) => ({
                          color: isActive ? 'var(--primary)' : 'var(--text-muted)',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                        })}
                      >
                        {item.labelTranslations?.[i18n.language] || item.label}
                      </NavLink>
                    ) : (
                      <a
                        href={absUrl}
                        style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.875rem' }}
                      >
                        {item.labelTranslations?.[i18n.language] || item.label}
                      </a>
                    )
                  })()}
                </span>
              ))}
            </p>
          )}
          <p>{t('layout.footer.email')}</p>
          <p>{siteSettings?.footerText || t('layout.footer.copyright', { year: new Date().getFullYear() })}</p>
          <p className="footer-links">
            <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer">{t('layout.sitemap')}</a>
          </p>
        </div>
      </footer>
      <AnalyticsConsent />
      <ChatWidget />
    </>
  )
}
